import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ClipboardList, Plus, Search, MoreHorizontal, Pencil, Trash2,
  ToggleLeft, ToggleRight, Eye, QrCode, FileSpreadsheet, RefreshCw,
  ExternalLink,
} from 'lucide-react'
import { useEncuestas, useEliminarEncuesta, useToggleEncuesta, useDescargarQR, useExportarExcel } from '../hooks/useEncuestas'
import { useTeam } from '@/shared/context/TeamContext'
import type { Encuesta } from '../types'

export function EncuestasListPage() {
  useDocumentTitle('Encuestas')
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEdit = user?.is_superuser || ['admin', 'encuestas_admin'].some(g => user?.groups?.includes(g))

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  // Filtrar por área seleccionada
  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'
  const areaGrupoId = isClientArea ? selectedArea?.laboratorio_id : undefined

  const { data, isLoading, refetch } = useEncuestas({ page, page_size: 10, search: search || undefined, grupo_laboratorio_id: areaGrupoId ?? undefined })
  const eliminarMutation = useEliminarEncuesta()
  const toggleMutation = useToggleEncuesta()
  const qrMutation = useDescargarQR()
  const excelMutation = useExportarExcel()

  const handleSearch = () => {
    setPage(1)
    setSearch(searchInput)
  }

  const handleDelete = async (encuesta: Encuesta) => {
    if (confirm(`¿Eliminar la encuesta "${encuesta.titulo}"?`)) {
      await eliminarMutation.mutateAsync(encuesta.id)
    }
  }

  const handleToggle = async (id: number) => {
    await toggleMutation.mutateAsync(id)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-black dark:text-white" />
          Encuestas
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          {canEdit && (
            <Button className="bg-teal-800 hover:bg-teal-900 text-white" size="sm" onClick={() => navigate('/encuestas/crear')}>
              <Plus className="mr-2 h-4 w-4" />
              Nueva Encuesta
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar encuestas..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>Buscar</Button>
      </div>

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : data && data.results.length > 0 ? (
          <div className="p-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableHead>Título</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-center">Respuestas</TableHead>
                  <TableHead className="text-center">Visualizaciones</TableHead>
                  <TableHead className="text-center">Conversión</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((encuesta) => (
                  <TableRow key={encuesta.id}>
                    <TableCell className="font-medium max-w-[250px] truncate">
                      <button
                        className="text-left hover:underline hover:text-blue-600 transition-colors"
                        onClick={() => navigate(`/encuestas/${encuesta.id}/editar`)}
                      >
                        {encuesta.titulo}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge className={encuesta.activa ? 'badge-gradient' : ''} variant={encuesta.activa ? 'default' : 'secondary'}>
                        {encuesta.activa ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{encuesta.total_respuestas}</TableCell>
                    <TableCell className="text-center">{encuesta.total_visualizaciones}</TableCell>
                    <TableCell className="text-center">{encuesta.tasa_conversion.toFixed(1)}%</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{encuesta.created_at}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${window.location.origin}/encuestas/responder/${encuesta.uuid}`, '_blank')}
                        title="Abrir URL pública"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && (
                            <DropdownMenuItem onClick={() => navigate(`/encuestas/${encuesta.id}/editar`)}>
                              <Pencil className="mr-2 h-4 w-4" /> Editar
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => navigate(`/encuestas/${encuesta.id}/preview`)}>
                            <Eye className="mr-2 h-4 w-4" /> Vista previa
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem onClick={() => handleToggle(encuesta.id)}>
                              {encuesta.activa
                                ? <><ToggleRight className="mr-2 h-4 w-4" /> Desactivar</>
                                : <><ToggleLeft className="mr-2 h-4 w-4" /> Activar</>
                              }
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => qrMutation.mutate(encuesta.id)}>
                            <QrCode className="mr-2 h-4 w-4" /> Descargar QR
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => excelMutation.mutate(encuesta.id)}>
                            <FileSpreadsheet className="mr-2 h-4 w-4" /> Exportar Excel
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(encuesta)}>
                              <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {(page - 1) * 10 + 1} - {Math.min(page * 10, data.count)} de {data.count}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                    Anterior
                  </Button>
                  <div className="text-sm">Página {page} de {data.total_pages}</div>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === data.total_pages}>
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <ClipboardList className="mx-auto h-12 w-12 text-teal-500" />
            <h3 className="mt-4 text-lg font-semibold">No se encontraron encuestas</h3>
            <p className="mt-2 text-sm text-muted-foreground">Crea una nueva encuesta para comenzar</p>
          </div>
        )}
      </Card>
    </div>
  )
}
